<template>
  <div class="min-h-screen bg-brand-bg">
    <!-- Navigation -->
    <nav class="relative bg-brand-blue/5 backdrop-blur-md border-b border-brand-blue/20 sticky top-0 z-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <!-- Logo -->
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <h1 class="text-2xl font-bold text-brand-blue">zC-Sign</h1>
            </div>
          </div>

          <!-- Desktop Navigation -->
          <div class="hidden md:block">
            <div class="ml-10 flex items-baseline space-x-8">
              <a href="#features" class="text-brand-blue hover:text-brand-green px-3 py-2 text-sm font-medium transition-colors cursor-pointer" @click="scrollToSection('features')">Features</a>
              <a href="#pricing" class="text-brand-blue hover:text-brand-green px-3 py-2 text-sm font-medium transition-colors cursor-pointer" @click="scrollToSection('pricing')">Pricing</a>
              <a href="#testimonials" class="text-brand-blue hover:text-brand-green px-3 py-2 text-sm font-medium transition-colors cursor-pointer" @click="scrollToSection('testimonials')">Testimonials</a>
            </div>
          </div>

          <!-- CTA Buttons -->
          <div class="hidden md:flex items-center space-x-4">
            <NuxtLink to="/login" class="text-brand-blue hover:text-brand-green px-3 py-2 text-sm font-medium transition-colors">
              Sign In
            </NuxtLink>
            <!-- Registration disabled -->
            <!-- <NuxtLink to="/register" class="bg-brand-green hover:bg-brand-blue text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-md">
              Create Account
            </NuxtLink> -->
          </div>

          <!-- Mobile menu button -->
          <div class="md:hidden">
            <button @click="isMenuOpen = !isMenuOpen" class="text-brand-blue hover:text-brand-green">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path v-if="!isMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Mobile Navigation -->
        <div v-if="isMenuOpen" class="md:hidden">
          <div class="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-brand-blue/5 border-t border-brand-blue/20">
            <a href="#features" class="text-brand-blue hover:text-brand-green block px-3 py-2 text-base font-medium">Features</a>
            <a href="#pricing" class="text-brand-blue hover:text-brand-green block px-3 py-2 text-base font-medium">Pricing</a>
            <a href="#testimonials" class="text-brand-blue hover:text-brand-green block px-3 py-2 text-base font-medium">Testimonials</a>
            <NuxtLink to="/login" class="text-brand-blue hover:text-brand-green block px-3 py-2 text-base font-medium">Sign In</NuxtLink>
            <!-- Registration disabled -->
            <!-- <NuxtLink to="/register" class="bg-brand-green hover:bg-brand-blue text-white block px-3 py-2 rounded-lg text-base font-medium text-center shadow-md">Get Started Free</NuxtLink> -->
          </div>
        </div>
      </div>
    </nav>

    <!-- Page Content -->
    <slot />

    <!-- Footer -->
    <footer class="bg-brand-bg text-brand-blue py-12">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <h3 class="text-2xl font-bold text-brand-blue mb-4">zC-Sign</h3>
            <p class="text-brand-green mb-4">
              The modern digital signature solution for businesses of all sizes.
            </p>
            <div class="flex space-x-4">
              <a href="#" class="text-brand-green hover:text-white transition-colors">
                <span class="sr-only">Twitter</span>
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"/>
                </svg>
              </a>
              <a href="#" class="text-brand-green hover:text-white transition-colors">
                <span class="sr-only">LinkedIn</span>
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>

          <div>
            <h4 class="text-lg font-semibold mb-4">Product</h4>
            <ul class="space-y-2">
              <li><a href="#" class="text-brand-blue hover:text-brand-green transition-colors">Features</a></li>
              <li><a href="#" class="text-brand-blue hover:text-brand-green transition-colors">Pricing</a></li>
              <li><a href="#" class="text-brand-blue hover:text-brand-green transition-colors">API</a></li>
              <li><a href="#" class="text-brand-blue hover:text-brand-green transition-colors">Integrations</a></li>
            </ul>
          </div>

          <div>
            <h4 class="text-lg font-semibold mb-4">Company</h4>
            <ul class="space-y-2">
              <li><a href="#" class="text-brand-blue hover:text-brand-green transition-colors">About</a></li>
              <li><a href="#" class="text-brand-blue hover:text-brand-green transition-colors">Blog</a></li>
              <li><a href="#" class="text-brand-blue hover:text-brand-green transition-colors">Careers</a></li>
              <li><a href="#" class="text-brand-blue hover:text-brand-green transition-colors">Contact</a></li>
            </ul>
          </div>

          <div>
            <h4 class="text-lg font-semibold mb-4">Support</h4>
            <ul class="space-y-2">
              <li><a href="#" class="text-brand-blue hover:text-brand-green transition-colors">Help Center</a></li>
              <li><a href="#" class="text-brand-blue hover:text-brand-green transition-colors">Documentation</a></li>
              <li><a href="#" class="text-brand-blue hover:text-brand-green transition-colors">Status</a></li>
              <li><a href="#" class="text-brand-blue hover:text-brand-green transition-colors">Security</a></li>
            </ul>
          </div>
        </div>

        <div class="border-t border-brand-green mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p class="text-brand-blue text-sm">
            © 2024 zC-Sign. All rights reserved.
          </p>
          <div class="flex space-x-6 mt-4 md:mt-0">
            <a href="#" class="text-brand-blue hover:text-brand-green text-sm transition-colors">Privacy Policy</a>
            <a href="#" class="text-brand-blue hover:text-brand-green text-sm transition-colors">Terms of Service</a>
            <a href="#" class="text-brand-blue hover:text-brand-green text-sm transition-colors">Cookie Policy</a>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
const isMenuOpen = ref(false)
</script>

<style scoped>
/* Smooth scrolling for anchor links */
html {
  scroll-behavior: smooth;
}
</style>
